import { findGaps, type Gap } from '../availability';
import { waMeLink } from '../phone';
import { buildReport, incomeAgorot, type ReportData, type ReportPeriodKind } from '../reports';
import { dayKeyOf, toMs } from '../time';
import type { ResolvedPlan } from '../trial';
import { fail, ok, type Appointment, type Client, type DayKey, type Id, type Message, type Result, type Service, type Staff } from '../types';
import { availabilityData, listAppointments, loadBundle, type Deps } from './context';
import type { OpenedSlotCard } from './waitlist';

export interface AgendaItem {
  appointment: Appointment;
  /** Never carries notes; fetch the client row for those. */
  client: { id: Id; name: string; phone: string; tags: string[]; hasSensitiveNotes: boolean } | undefined;
  service: Pick<Service, 'id' | 'name' | 'durationMin'> | undefined;
  staff: Pick<Staff, 'id' | 'name' | 'colorKey'> | undefined;
}
export interface TodayView {
  day: DayKey;
  nowIso: string;
  business: { id: Id; name: string; slug: string };
  plan: ResolvedPlan;
  /** Chronological, cancelled ones excluded. */
  items: AgendaItem[];
  /** First not-yet-finished item. */
  next: AgendaItem | undefined;
  stats: { appointments: number; expectedIncomeAgorot: number; unconfirmed: number; cancelled: number };
  /** Tappable "פנוי · N דק׳" rows. */
  gaps: Gap[];
  /** "נפתח חלון ..." cards for freed slots with waiting clients. */
  opened: OpenedSlotCard[];
}
export interface ManualQueueItem { message: Message; waLink: string; text: string }

const SHOWN = new Set(['awaiting_deposit', 'pending', 'confirmed', 'completed', 'no_show']);

export function viewUseCases(d: Deps, openedSlotCards: (b: NonNullable<Awaited<ReturnType<typeof loadBundle>>>, a: Appointment[]) => Promise<OpenedSlotCard[]>) {
  async function getTodayView(businessId: Id, day?: DayKey): Promise<Result<TodayView, 'business_not_found'>> {
    const b = await loadBundle(d, businessId);
    if (!b) return fail('business_not_found');
    const nowMs = d.clock.now();
    const today = day ?? dayKeyOf(nowMs, b.business.tz);
    const appointments = await listAppointments(d, businessId);
    const clients = new Map((await d.storage.repo('clients').list({ where: { businessId } })).map((c) => [c.id, c]));
    const toItem = (a: Appointment): AgendaItem => {
      const c: Client | undefined = clients.get(a.clientId);
      const s = b.services.find((x) => x.id === a.serviceId);
      const st = b.staff.find((x) => x.id === a.staffId);
      return {
        appointment: a,
        client: c ? { id: c.id, name: c.name, phone: c.phone, tags: c.tags, hasSensitiveNotes: c.sensitiveNotes.trim() !== '' } : undefined,
        service: s ? { id: s.id, name: s.name, durationMin: s.durationMin } : undefined,
        staff: st ? { id: st.id, name: st.name, colorKey: st.colorKey } : undefined,
      };
    };
    const ofDay = appointments.filter((a) => dayKeyOf(toMs(a.start), b.business.tz) === today).sort((x, y) => x.start.localeCompare(y.start));
    const items = ofDay.filter((a) => SHOWN.has(a.status)).map(toItem);
    const shortest = Math.max(15, Math.min(...b.services.filter((s) => s.active).map((s) => s.durationMin), 60));
    return ok({
      day: today, nowIso: d.clock.nowIso(), business: { id: businessId, name: b.business.name, slug: b.business.slug }, plan: b.plan,
      items, next: items.find((i) => toMs(i.appointment.end) > nowMs && i.appointment.status !== 'completed' && i.appointment.status !== 'no_show'),
      stats: {
        appointments: items.length,
        expectedIncomeAgorot: incomeAgorot(items.map((i) => i.appointment)),
        unconfirmed: items.filter((i) => i.appointment.status === 'pending' || i.appointment.status === 'awaiting_deposit').length,
        cancelled: ofDay.length - items.length,
      },
      gaps: findGaps(availabilityData(b, appointments), today, nowMs, shortest),
      opened: await openedSlotCards(b, appointments),
    });
  }

  async function getReport(businessId: Id, opts: { period: ReportPeriodKind; anchor?: DayKey }): Promise<Result<ReportData, 'business_not_found'>> {
    const b = await loadBundle(d, businessId);
    if (!b) return fail('business_not_found');
    const nowMs = d.clock.now();
    return ok(buildReport({
      appointments: await listAppointments(d, businessId), services: b.services, kind: opts.period,
      anchor: opts.anchor ?? dayKeyOf(nowMs, b.business.tz), nowMs, tz: b.business.tz,
    }));
  }

  /** Free plan: reminders waiting for the owner to send by hand (one-tap wa.me). */
  async function getManualQueue(businessId: Id): Promise<ManualQueueItem[]> {
    const rows = await d.storage.repo('messages').list({ where: { businessId, status: 'suppressed' }, filter: (m) => m.suppressionReason === 'plan_free_manual' });
    return rows.sort((a, c) => a.scheduledFor.localeCompare(c.scheduledFor)).map((message) => ({ message, text: message.text ?? '', waLink: waMeLink(message.to, message.text) }));
  }
  const markManualSent = (messageId: Id): Promise<void> => d.messaging.markManualSent(messageId);

  return { getTodayView, getReport, getManualQueue, markManualSent };
}
